import { useTranslation } from 'react-i18next';
import { formatDateTime } from '~/utils';
import { RequestForQuoteStatus } from '~/graphql/graphql';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { FulfillRequestForQuoteButton } from '~/pages/rfq-view/sections/FulfillRequestForQuoteButton';
import { comparedRequestRowElementId } from '~/utils/requestForQuoteLinks';
import type { ComparableRequestForQuote } from './aggregateRequests';

export type ComparedRequestForQuote = Pick<
  ComparableRequestForQuote,
  'id' | 'status' | 'createdAt' | 'createdByUserName' | 'quotes' | 'items'
>;

type ComparedRequestRowsProps = {
  requestsForQuote: ComparedRequestForQuote[];
};

const CLOSED_STATUS_LABEL_KEYS: Partial<Record<RequestForQuoteStatus, string>> =
  {
    [RequestForQuoteStatus.Archived]: 'rfq_view.request_archived_label',
    [RequestForQuoteStatus.Fulfilled]: 'rfq_view.request_fulfilled_label',
  };

export function ComparedRequestRows({
  requestsForQuote,
}: Readonly<ComparedRequestRowsProps>) {
  const { t } = useTranslation('rfq-view');

  return (
    <ul className="flex flex-col gap-1">
      {requestsForQuote.map((requestForQuote) => {
        const closedStatusLabelKey =
          CLOSED_STATUS_LABEL_KEYS[requestForQuote.status];
        return (
          <li
            key={requestForQuote.id}
            id={comparedRequestRowElementId(requestForQuote.id)}
            className="flex flex-wrap items-center gap-2 text-sm text-neut-sec"
            data-testid={`compared-request-row-${requestForQuote.id}`}
          >
            <span>
              {t('rfq_view.quote_comparison.request_sent', {
                sentAt: formatDateTime(requestForQuote.createdAt),
              })}
              {requestForQuote.createdByUserName &&
                ` · ${t('rfq_view.quote_comparison.request_created_by', {
                  name: requestForQuote.createdByUserName,
                })}`}
            </span>
            {closedStatusLabelKey && (
              <span
                className={statusBadgeClasses('neutral')}
                data-testid={`compared-request-status-${requestForQuote.id}`}
              >
                {t(closedStatusLabelKey)}
              </span>
            )}
            <FulfillRequestForQuoteButton
              requestForQuote={requestForQuote}
              quotes={requestForQuote.quotes}
            />
          </li>
        );
      })}
    </ul>
  );
}
